import { FiCheck } from "react-icons/fi";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn, formatCents } from "@/lib/utils";
import {
  OIC_STEPS,
  oicStepIndex,
  type OICStep,
  type OICSummary,
} from "@/features/oic/types";

interface Props {
  currentStep: OICStep;
  onSelect: (step: OICStep) => void;
  summary: OICSummary | null;
  /** Steps at/after `eligibility` are locked when the CSED has expired. */
  ineligible?: boolean;
}

const LOCKED_AFTER = ["terms", "packet"] as const;

/**
 * The persistent OIC wizard rail: ordered steps with completion ticks + the
 * current step highlighted, plus an offer chip (lump-sum offer) once the RCP
 * summary is loaded. Terms/Packet are locked (not navigable) when the offer is
 * CSED-ineligible.
 */
export function OicRail({ currentStep, onSelect, summary, ineligible }: Props) {
  const activeIdx = oicStepIndex(currentStep);

  return (
    <Card className="gap-4 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold">Offer in Compromise</h3>
        <OfferChip summary={summary} ineligible={ineligible} />
      </div>
      <ol className="grid gap-1">
        {OIC_STEPS.map((step, i) => {
          const isCurrent = step.key === currentStep;
          const isDone = i < activeIdx;
          const locked = !!ineligible && (LOCKED_AFTER as readonly string[]).includes(step.key);
          return (
            <li key={step.key}>
              <button
                type="button"
                disabled={locked}
                onClick={() => !locked && onSelect(step.key)}
                aria-current={isCurrent ? "step" : undefined}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors",
                  locked && "cursor-not-allowed opacity-40",
                  isCurrent
                    ? "bg-primary/10 text-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                <span
                  className={cn(
                    "grid size-6 shrink-0 place-items-center rounded-full border text-xs",
                    isDone
                      ? "border-primary bg-primary text-primary-foreground"
                      : isCurrent
                        ? "border-primary text-primary"
                        : "border-muted-foreground/40",
                  )}
                >
                  {isDone ? <FiCheck className="size-3.5" /> : i + 1}
                </span>
                {step.label}
              </button>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

function OfferChip({ summary, ineligible }: { summary: OICSummary | null; ineligible?: boolean }) {
  if (ineligible) {
    return (
      <Badge variant="destructive" className="whitespace-nowrap">
        CSED expired
      </Badge>
    );
  }
  if (!summary?.available || summary.lumpSumOfferCents == null) return null;
  return (
    <Badge variant="secondary" className="whitespace-nowrap">
      Offer from {formatCents(summary.lumpSumOfferCents)}
    </Badge>
  );
}
